import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { User, CreditCard, BarChart3, CheckCircle, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import ConfirmDialog from '../components/ConfirmDialog';
import fetchWithTimeout from '../lib/fetchWithTimeout';
import { isNativePlatform, isIOS } from '../lib/capacitor';
import { API_URL as BACKEND_URL } from '../lib/apiUrl';
import { Browser } from '@capacitor/browser';

export default function Account() {
  const { user, tier, getAccessToken, refreshProfile, signOut } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [usage, setUsage] = useState({ basic_count: 0, enhanced_count: 0 });
  const [limits, setLimits] = useState({ daily_basic: 3, monthly_enhanced: 0 });
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [searchParams] = useSearchParams();

  const showSuccess = searchParams.get('success') === 'true';

  useEffect(() => {
    if (showSuccess) {
      // Refresh profile after successful payment
      refreshProfile();
    }
    fetchUsage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuccess]);

  const fetchUsage = async () => {
    try {
      const token = await getAccessToken();
      if (!token) return;

      const response = await fetchWithTimeout(`${BACKEND_URL}/api/user/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch usage');
      setUsage(data.usage || { basic_count: 0, enhanced_count: 0 });
      setLimits(data.limits || { daily_basic: 3, monthly_enhanced: 0 });
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const handleManageSubscription = async () => {
    // On native, open website for subscription management
    if (isNativePlatform()) {
      await Browser.open({ url: 'https://ridesharechecker.com/pricing' });
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Could not open billing portal');
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.error || 'Could not open billing portal');
      }
    } catch (error) {
      if (error.message?.includes('No subscription')) {
        await refreshProfile();
        window.location.href = '/pricing';
      } else {
        toast.error(error.message || 'Error opening billing portal');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const token = await getAccessToken();
      const response = await fetchWithTimeout(`${BACKEND_URL}/api/user/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete account');

      // Sign out and redirect
      await signOut();
      navigate('/');
      toast.success('Your account has been permanently deleted.');
    } catch (error) {
      toast.error(error.message || 'Error deleting account. Please contact support.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getTierDisplay = (t) => {
    return t.charAt(0).toUpperCase() + t.slice(1);
  };

  if (!user) {
    return (
      <div className="app-container">
        <div className="app-wrapper">
          <div className="app-card">
            <div className="app-header">
              <h1>Account</h1>
              <p>Please sign in to view your account</p>
            </div>
            <div className="login-section">
              <Link to="/login" className="btn btn-yellow btn-large">Sign In</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="app-wrapper">
        <div className="app-card">
          <div className="app-header">
            <h1>
              <User className="header-icon" />
              My Account
            </h1>
            <p>Manage your subscription and usage</p>
          </div>

          <div className="account-section">
            {showSuccess && (
              <div className="success-banner">
                <CheckCircle size={20} />
                <span>Payment successful! Your account has been upgraded.</span>
              </div>
            )}

            {/* Profile Info */}
            <div className="account-card">
              <h3><User size={18} /> Profile</h3>
              <div className="account-info">
                <div className="info-row">
                  <span className="label">Email:</span>
                  <span className="value">{user.email}</span>
                </div>
                <div className="info-row">
                  <span className="label">Plan:</span>
                  <span className={`value tier-badge tier-${tier}`}>
                    {getTierDisplay(tier)}
                  </span>
                </div>
              </div>
            </div>

            {/* Usage Stats */}
            <div className="account-card">
              <h3><BarChart3 size={18} /> Usage</h3>
              <div className="usage-stats">
                <div className="usage-item">
                  <div className="usage-label">Basic Lookups Today</div>
                  <div className="usage-value">
                    {usage.basic_count} / {limits.daily_basic === -1 ? '∞' : limits.daily_basic}
                  </div>
                  {limits.daily_basic !== -1 && (
                    <div className="usage-bar">
                      <div
                        className="usage-fill"
                        style={{ width: `${Math.min((usage.basic_count / limits.daily_basic) * 100, 100)}%` }}
                      />
                    </div>
                  )}
                </div>
                {!(isIOS() && limits.monthly_enhanced === 0) && (
                  <div className="usage-item">
                    <div className="usage-label">Enhanced Lookups This Month</div>
                    <div className="usage-value">
                      {limits.monthly_enhanced === 0
                        ? 'Upgrade for enhanced lookups'
                        : `${usage.enhanced_count} / ${limits.monthly_enhanced}`}
                    </div>
                    {limits.monthly_enhanced > 0 && (
                      <div className="usage-bar">
                        <div
                          className="usage-fill enhanced"
                          style={{ width: `${Math.min((usage.enhanced_count / limits.monthly_enhanced) * 100, 100)}%` }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Subscription Management — hidden on iOS for App Store compliance.
                Apple disallows non-IAP payment links and prohibits steering users to
                external billing pages. Subscriptions remain manageable on the web. */}
            {!isIOS() && (
              <div className="account-card">
                <h3><CreditCard size={18} /> Subscription</h3>
                <div className="subscription-actions">
                  {tier === 'free' ? (
                    <Link to="/pricing" className="btn btn-yellow">
                      Upgrade to Pro
                    </Link>
                  ) : (
                    <>
                      {tier !== 'enterprise' && (
                        <Link to="/pricing" className="btn btn-yellow">
                          Upgrade Plan
                        </Link>
                      )}
                      <button
                        onClick={handleManageSubscription}
                        disabled={loading}
                        className="btn btn-dark"
                      >
                        {loading ? 'Loading...' : 'Manage Subscription'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Legal Links */}
            <div className="account-card">
              <h3>Legal</h3>
              <div className="legal-links-list">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/terms">Terms of Service</Link>
                <Link to="/licenses">Open Source Licenses</Link>
              </div>
            </div>

            {/* Delete Account */}
            <div className="account-card account-card-danger">
              <h3><Trash2 size={18} /> Delete Account</h3>
              <p className="delete-account-warning">
                Permanently delete your account and all associated data including search history,
                shared trips, and usage data. If you have an active subscription, it will be cancelled.
                This action cannot be undone.
              </p>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                className="btn btn-red"
              >
                <Trash2 size={16} />
                {deleting ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>

            <ConfirmDialog
              isOpen={showDeleteConfirm}
              title="Delete Account Permanently?"
              message={`This will permanently delete your account (${user.email}), cancel any active subscription, and remove all your data including search history and shared trips. This cannot be undone.`}
              confirmText={deleting ? 'Deleting...' : 'Yes, Delete Everything'}
              cancelText="Cancel"
              onConfirm={handleDeleteAccount}
              onCancel={() => setShowDeleteConfirm(false)}
              variant="warning"
            />

            <div className="account-footer">
              <Link to="/">Back to App</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
